<!--shows the properties views of an orgObj passed to it.
    needs an org object with orgId as one of the keys
    you will probably have to do something like this

    <ehc-properties :orgObj="{...org, orgId: value}"></ehc-properties>

    otherwise you shouldn't really need to do anything. 
 -->
<template>
  <div class="ehc-properties">
    <span v-if="userIsAdmin"
      >      {{ gcvOrg.name }} has {{ propCount }} property guides of
      {{ gcvOrgPropertyLimit }} allowed. </span
    ><br />
    <span
      v-if="userIsAdmin"
      class="text-caption pointer"
      @click="goToOrg(gcvOrg.key)"
      >{{ gcvOrg.key }}</span
    >
    <v-alert
      v-if="alertMessage.show"
      @click="goToSubscribePage"
      class="warning pointer"
      ><p class="text-center text-h6" v-html="alertMessage.message"></p>
      <v-btn @click="goToSubscribePage" block>Subscribe NOW</v-btn></v-alert
    >

    <p v-if="showDebug">
      <pre>
      {{ orgIsSubscribed ? " is subscribed" : " is not subscribed" }}
      {{ gcvOrg.account }}
      </pre>
    </p>

    <v-alert
      v-for="alert in visiblePropertyPageAlerts"
      :key="alert.id"
      :value="true"
      :type="alert.type || 'info'"
      outlined
      prominent
      dismissible
      class="mb-4"
      :icon="alert.icon || 'mdi-information-outline'"
      @input="onPropertyPageAlertDismiss(alert)"
    >
      <div class="font-weight-bold mb-1">{{ alert.title }}</div>
      <div>{{ alert.body }}</div>
    </v-alert>

    <ehc-header
      :text="headerText"
      :actions="headerActions"
      @addProperty="addProperty()"
      @importFromListing="openListingImport()"
      @viewChange="view = $event"
      @backfillCardViewsDryRun="runMigratePropertyCardViews(true)"
      @backfillCardViewsLive="runMigratePropertyCardViews(false)"
    ></ehc-header>
    <v-progress-linear
      v-if="loading"
      indeterminate
      color="primary"
      class="mt-n2 mb-3"
    ></v-progress-linear>
    <v-alert
      v-if="sidecarRunResult"
      :type="sidecarRunResult.success ? 'success' : 'error'"
      outlined
      class="mb-4"
    >
      <div class="font-weight-bold mb-2">
        {{ sidecarRunResult.title }}
      </div>
      <div v-if="sidecarRunResult.message" class="mb-2">
        {{ sidecarRunResult.message }}
      </div>
      <div
        v-if="sidecarRunResult.summary"
        class="text-caption"
        style="white-space: pre-wrap"
      >
        {{ formatSidecarSummary(sidecarRunResult.summary) }}
      </div>
    </v-alert>

    <v-card flat class="mb-4 pa-4" outlined>
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-text-field
            v-model="searchInput"
            placeholder="Search by name, ID, city, state, or address"
            outlined
            dense
            clearable
            prepend-icon="mdi-magnify"
            @input="debouncedSearch"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card>

    <ehc-prop-view-decider
      :view="view"
      :properties="filteredProperties"
      :actions="actions"
      :loading="loading"
      @deleteProperty="message = { ...confirmDelete, prop: $event }"
      @publishProperty="publishProperty($event)"
      @showInterview="showInterview($event.propertyId)"
      @showAnalytics="showAnalytics($event.propertyId)"
      @showGuestQABuilder="showGuestQABuilder($event.propertyId)"
      @downloadPropertyPdf="downloadPropertyGuidePdf($event)"
      @goToPreview="goToPreview($event.propertyId)"
      @goToLocalhost="goToLocalhost($event.propertyId)"
      @propertyLink="showPropertyLink($event)"
      @propertyQRCode="showPropertyQRCode($event)"
      @editProperty="goToProperty($event)"
      @goToDefault="goToDefault"
      @showTransferProperty="showTransferProperty($event.propertyId)"
      @copyProperty="copyProperty($event)"
      @guestInfo="setGuestInfo($event)"
      @goToOrg="goToOrg($event)"
      @goToFirestoreProperty="goToFirestoreProperty($event)"
      @syncPropertyCardView="syncPropertyCardViewFromProperty($event)"
      :endOfData="true"
    ></ehc-prop-view-decider>
    <div
      v-if="showRetrieveAllOption || orgObj"
      class="text-caption text-right mt-2 mr-2"
    >
      Showing {{ filteredProperties.length }}<span
        v-if="searchQuery && filteredProperties.length !== properties.length"
      >
        of {{ properties.length }}</span
      >
      properties
    </div>

    <ehc-message
      @cancel="message = closeMessage"
      @cancelPdfDownload="cancelPropertyGuidePdfDownload()"
      @downloadGuidePdf="downloadGuideAsPdf()"
      @downloadGuideDoc="downloadGuideAsDoc()"
      @deleteProp="deleteProperty(message.prop)"
      @confirmEditDefaultTemplate="confirmEditDefaultTemplateProceed"
      @nextInterview="nextInterview($event)"
      @closeInterview="closeInterview()"
      @closeGuestQA="closeGuestQABuilder()"
      v-model="message"
    >
      <template #link>
        Link to {{ message.prop.name }} guide has been copied to your clipboard
        <br /><br />
        <p class="text-h5 text-center" v-if="!message.prop.publishedAt">
          This property has not been published yet.
        </p>
      </template>

      <template #qrcode>
        <qrcode
          @downloadQRCodeImage="downloadQRCodeImage"
          :value="message.link"
          :showTitle="true"
          :showBigButton="false"
        />
      </template>

      <template #maxProps>
        <h5
          v-html="maxPropertiesMessage.title"
          style="word-wrap: break-word; white-space: normal"
        ></h5>
      </template>

      <template #transferProp>
        <span style="font-size: 0.8em"
          >This feature is in 'beta'. Enter a valid organization ID.<br
        /></span>
        <ehc-transfer-prop
          :value="message"
          @transferComplete="onPropertyTransferred"
          @closeTransfer="closeTransfer"
        ></ehc-transfer-prop>
      </template>

      <template #interview>
        <ehc-interview
          :propertyId="message.propertyId"
          :page="message.page"
          @change="message.formData = $event"
          @closeInterview="closeInterview"
          :trigger="message.trigger"
          :key="propertyId"
        ></ehc-interview>
      </template>

      <template #analytics>
        <ehc-analytics-guest
          close
          :propertyId="message.propertyId"
          :guestInfo="message.guestInfo"
          @closeAnalytics="closeAnalytics"
        >
        </ehc-analytics-guest>
      </template>

      <template #guestQA>
        <ehc-guest-qa-builder
          :propertyId="message.propertyId"
          @saved="closeGuestQABuilder"
          @unansweredCountChange="setUnansweredGuestQuestionCount"
        ></ehc-guest-qa-builder>
      </template>
    </ehc-message>

    <ehc-listing-import
      v-if="orgObj && orgObj.orgId"
      v-model="showListingImport"
      :orgId="orgObj.orgId"
      @created="onListingImportCreated"
    />
  </div>
</template>

<script>
import EhcPropertiesTable from "@/components/properties/ehc-properties-table.vue";
import EhcPropertiesGallery from "@/components/properties/ehc-properties-gallery.vue";
import EhcPropViewDecider from "@/components/properties/ehc-prop-view-decider.vue";
import EhcInterview from "@/components/properties/ehc-interview.vue";
import EhcAnalyticsGuest from "@/components/ehc-analytics-guest.vue";
import EhcGuestQaBuilder from "@/components/properties/ehc-guest-qa-builder.vue";
import EhcListingImport from "@/components/properties/ehc-listing-import.vue";
import EhcTransferProp from "@/components/properties/ehc-transfer-prop.vue";

import qrcode from "@/components/ehc-qrcode.vue";

//get index mixin
import Formating from "@/mixins/index.vue";
import firebase from "firebase";
import ehcAnalyticsGuestVue from "../ehc-analytics-guest.vue";
import PropertyActionsVue from "../../mixins/propertyActions.vue";
import {
  resolvePropertyPageAlerts,
  shouldShowPropertyPageAlert,
  recordPropertyPageAlertView,
  readPropertyPageAlertState,
  writePropertyPageAlertState,
} from "@/utils/propertyPageAlerts.js";

const { hasFeature } = require("@/utils/entitlements.js");

export default {
  components: {
    EhcPropertiesTable,
    EhcPropertiesGallery,
    EhcPropViewDecider,
    EhcInterview,
    EhcTransferProp,
    EhcAnalyticsGuest,
    EhcGuestQaBuilder,
    EhcListingImport,
    qrcode,
  },
  mixins: [Formating, PropertyActionsVue],
  props: {
    orgObj: { type: [Object, Boolean], default: false },
    headerText: { type: String, default: "Properties" },
    showRetrieveAllOption: { type: Boolean, default: false },
  },
  data() {
    return {
      showListingImport: false,
      closeDialog: {
        show: false,
        prop: {},
        message: "",
        link: null,
        orgId: null,
        moveGuestInfo: false,
      },
      message: {
        show: true,
      },
      // transferPropMessage: {
      //     show: true,
      //     title: "Transfer Property",
      //     message: "TODO: this feature is broken, if we really need it then it should be fixed. ",
      //     slotName: "transferProp",
      //     actions: [
      //         {type: "spacer"},
      //         {label: "ok", clickEmit: "cancel"}
      //     ]
      // },

      closeMessage: {
        show: false,
        propertyId: null,
      },
      confirmDelete: {
        show: true,
        title: "Are You Sure?",
        message: "are you sure you want to delete this property?",
        width: 350,
        persistent: false,
        loading: false,
        actions: [
          { label: "yes delete", clickEmit: "deleteProp", color: "error" },
          { type: "spacer" },
          { label: "cancel", clickEmit: "cancel", color: "success" },
        ],
      },
      transferPropertyDialog: {
        show: false,
        prop: {},
        orgId: null,
        moveGuestInfo: false,
        actions: [
          { label: "cancel", clickEmit: "cancel", dark: false, plain: true },
          { type: "spacer" },
        ],
      },
      linkDialog: {
        show: false,
        prop: {},
      },
      message: {
        show: false,
      },
      maxPropsDialog: false,
      properties: [],
      searchInput: "",
      searchQuery: "",
      searchTimeout: null,
      guestQAOpenedFromRoute: false,
      view: "gallery",
      loading: false,
      propertyId: null,
      // downloadQRCodeImage: null,
      showDebug: false,
      sendNewPropertyEmail: false, //set to true to send email on new property creation, firebase function also sends email independently
      runningSidecarJob: false,
      sidecarRunResult: null,
      visiblePropertyPageAlerts: [],
    };
  },
  watch: {
    user() {
      this.syncPropertyPageAlerts();
    },
    gcvAppSettings() {
      this.syncPropertyPageAlerts();
    },
    orgObj(newOrg, oldOrg) {
      // console.log("properties org change", this.orgObj)
      if (newOrg.orgId != oldOrg.orgId) {
        this.properties = [];
        this.getProperties();
      }
    },
    // Uncomment when you want to show interview on new property when showInterviewOnNewProperty is true
    propertyId(value) {
      if (this.showInterviewOnNewProperty) {
        this.showInterview(value);
      } else {
        console.log(
          "showInterviewOnNewProperty is ",
          this.showInterviewOnNewProperty
        );
      }
    },
    // message(){
    //     console.log("message changed in ehc-properties", this.message)
    // },
    // "message.trigger"(){
    //     console.log("message.trigger changed in ehc-properties", this.message.trigger)
    // },
    "$route.query.search"(value) {
      this.applySearchFromRouteQuery(value);
    },
    "$route.query.guestQA"() {
      this.maybeOpenGuestQAFromRoute();
    },
    filteredProperties() {
      this.maybeOpenGuestQAFromRoute();
    },
  },
  mounted() {
    // console.log("properties mounted", this.orgObj)
    this.applySearchFromRouteQuery();
    this.getProperties();
    this.syncPropertyPageAlerts();
    if (this.$route.query.showDebug !== undefined) {
      this.showDebug = true;
    }
  },
  computed: {
    hasListingImportAccess() {
      const org =
        this.orgObj && this.orgObj !== false ? this.orgObj : this.gcvOrg;
      return hasFeature(org, "listingImport");
    },
    alertMessage() { return {
        show: !this.orgIsSubscribed ? true : false,
        title: "Alert",
        message: this.gcvAppSettings.subscribeMessage || "This Organization is not subscribed",
        actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
      };
    },
    maxPropertiesMessageTitle() {
      if (this.orgIsSubscribed) {
        return `Your properties limit has been reached (${this.limit}).`;
      } else {
        return `This organization is not currently subscribed. <a href="javascript:void(0)" onclick="window.location.href='/premium'">Subscribe now</a> to add more properties.`;
      }
    },
    maxPropertiesMessage() {
      return {
        show: true,
        title: this.maxPropertiesMessageTitle,
        slotName: "maxProps",
        actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
      };
    },
    limit() {
      return this.gcvOrgPropertyLimit;
    },
    count() {
      return this.properties.length;
    },
    filteredProperties() {
      const search = (this.searchQuery || "").trim().toLowerCase();
      if (!search) {
        return this.properties;
      }
      return this.properties.filter((prop) => {
        const fields = [
          prop.name,
          prop.propertyId,
          prop.city,
          prop.state,
          prop.mapAddress?.formatted_address,
        ]
          .filter(Boolean)
          .map((value) => String(value).toLowerCase());
        return fields.some((field) => field.includes(search));
      });
    },
    actions() {
      let items = [
        {
          type: "link",
          label: "Edit",
          iconURL: require("@/assets/icons/edit@3x.svg"),
          emitClick: "editProperty",
          toolTip: "Edit property",
        },
        {
          type: "link",
          label: "Link",
          iconURL: require("@/assets/icons/Link@3x.svg"),
          emitClick: "propertyLink",
          toolTip: "Get property QR Code & link",
        },
        {
          type: "link",
          label: "QR Code",
          iconURL: require("@/assets/icons/qrcode.svg"),
          emitClick: "propertyQRCode",
          toolTip: "Get property QR Code & link",
        },
        {
          type: "link",
          label: "Delete",
          iconURL: require("@/assets/icons/trash@3x.svg"),
          emitClick: "deleteProperty",
          toolTip: "Delete property",
        },
        {
          type: "toggle",
          key: "publishedAt",
          onTrue: {
            label: "Unpublish",
            iconURL: require("@/assets/icons/unpulish@3x.svg"),
            emitClick: "publishProperty",
            toolTip: "click to unpublish property",
          },
          onFalse: {
            label: "Publish",
            iconURL: require("@/assets/icons/unpulish@3x.svg"),
            emitClick: "publishProperty",
            toolTip: "click to publish property",
          },
        },
        {
          type: "link",
          label: "Copy",
          iconURL: require("@/assets/icons/Copy@3x.svg"),
          emitClick: "copyProperty",
          toolTip: "Copy property",
        },
        {
          type: "toggle",
          key: "noguestinfo",
          onFalse: {
            label: "Do not ask for guest info",
            icon: "mdi-clipboard-account-outline",
            emitClick: "guestInfo",
            toolTip: "Copy property",
          },
          onTrue: {
            label: "Ask for guest info",
            icon: "mdi-clipboard-account-outline",
            emitClick: "guestInfo",
            toolTip: "Copy property",
          },
        },
        {
          type: "toggle",
          key: "setupCoachCompletedAt",
          onFalse: {
            label: "Finish setup",
            icon: "mdi-school-outline",
            emitClick: "showInterview",
            toolTip: "Finish setting up your digital guide",
          },
          onTrue: {
            label: "Set up your guide",
            icon: "mdi-school-outline",
            emitClick: "showInterview",
            toolTip: "Re-run New Owner Setup",
          },
        },
        ...this.propertyAnalyticsMenuItems,
        ...this.propertyGuestQAMenuItems,
        ...this.propertyPdfMenuItems,
        ...this.userHasPermission(
          ["superAdmin"],
          [
            { type: "subheader", label: "Admin" },
            { type: "divider" },
            {
              type: "link",
              label: "Firestore",
              icon: "mdi-database-outline",
              emitClick: "goToFirestoreProperty",
              toolTip: "Open this property in Firebase console",
            },
            {
              type: "link",
              label: "Sync Card View",
              icon: "mdi-database-sync-outline",
              emitClick: "syncPropertyCardView",
              toolTip:
                "Update propertyCardViews for this property from properties",
            },
            {
              type: "link",
              label: "Transfer",
              icon: "mdi-swap-horizontal",
              emitClick: "showTransferProperty",
              toolTip: "Transfer property to another account",
            },
            {
              type: "link",
              label: "View Organization",
              iconURL: require("@/assets/icons/Home@3xplain.svg"),
              emitClick: "goToOrg",
              toolTip: "view the organization of this property",
            },
            {
              type: "link",
              label: "Set up your guide",
              icon: "mdi-school-outline",
              emitClick: "showInterview",
              toolTip: "New Owner Setup — configure your digital guide",
            },
            {
              type: "link",
              label: "Localhost",
              icon: "mdi-eye-arrow-right",
              emitClick: "goToLocalhost",
              toolTip: "Preview of Dev",
            },
            {
              type: "link",
              label: "Property Template",
              icon: "mdi-copyleft",
              emitClick: "goToDefault",
              toolTip: "Default Template",
            },
          ],
          []
        ),
      ];
      return items;
    },
    appSite() {
      return this.$store.getters.appSite;
    },
    propLimit() {
      return this.gcvOrgPropertyLimit;
    },
    headerActions() {
      const adminMenuItems = this.showRetrieveAllOption
        ? [
            {
              type: "link",
              label: "Backfill Card Views Dry Run",
              icon: "mdi-play-circle-outline",
              emitClick: "backfillCardViewsDryRun",
              toolTip: "Preview propertyCardViews backfill",
            },
            {
              type: "link",
              label: "Backfill Card Views Now",
              icon: "mdi-database-sync-outline",
              emitClick: "backfillCardViewsLive",
              toolTip: "Create/update propertyCardViews docs",
            },
          ]
        : [];

      let adminActions = this.userHasPermission(
        ["superAdmin"],
        adminMenuItems.length
          ? [
              {
                actionType: "menu",
                icon: "mdi-database-outline",
                items: adminMenuItems,
              },
            ]
          : [],
        []
      );

      let actions = [
        {
          actionType: "v-btn-toggle",
          dense: true,
          changeEmit: "viewChange",
          default: "gallery",
          mandatory: true,
          buttons: [
            {
              label: "Grid View",
              value: "gallery",
              outlined: true,
              prependIconURL: require("@/assets/icons/Grid View@3x.svg"),
            },
            {
              label: "List View",
              value: "table",
              outlined: true,
              prependIconURL: require("@/assets/icons/List View@3x.svg"),
            },
          ],
        },
      ];
      let orgActions =
        this.orgObj != false
          ? [
              ...(this.hasListingImportAccess
                ? [
                    {
                      actionType: "v-btn",
                      label: "Import from listing",
                      color: "primary",
                      outlined: true,
                      dark: false,
                      prependIcon: "mdi-link-plus",
                      clickEmit: "importFromListing",
                    },
                  ]
                : []),
              {
                actionType: "v-btn",
                label: "Set up your guidebook",
                color: "primary",
                dark: true,
                prependIcon: "mdi-plus",
                clickEmit: "addProperty",
              },
            ]
          : [];

      return [...adminActions, ...actions, ...orgActions];
    },
    showInterviewOnNewProperty: function () {
      // New Owner Setup opens after property creation by default.
      try {
        const setting = this.$store.getters.appSettings?.showInterviewOnNewProperty;
        if (setting === false) {
          return false;
        }
        return true;
      } catch (err) {
        console.log(err);
        return true;
      }
    },
    propCount: function () {
      return this.properties.length;
    },
    publishedPropCount: function () {
      let count = 0;
      this.properties.forEach((prop) => {
        if (prop.publishedAt) {
          count++;
        }
      });
      return count;
    },
    unPublishedPropCount: function () {
      let count = 0;
      this.properties.forEach((prop) => {
        if (!prop.publishedAt) {
          count++;
        }
      });
      return count;
    },
  },
  methods: {
    syncPropertyPageAlerts() {
      if (!this.loggedIn || !this.user?.uid || this.showRetrieveAllOption) {
        this.visiblePropertyPageAlerts = [];
        return;
      }

      const uid = this.user.uid;
      const candidates = resolvePropertyPageAlerts(this.gcvAppSettings || {});
      const visible = [];

      candidates.forEach((alert) => {
        if (!shouldShowPropertyPageAlert(alert, uid)) {
          return;
        }
        // Count at most once per tab session so HMR/remounts don't burn maxViews.
        recordPropertyPageAlertView(alert.id, uid);
        visible.push(alert);
      });

      this.visiblePropertyPageAlerts = visible;
    },
    onPropertyPageAlertDismiss(alert) {
      const uid = this.user?.uid;
      if (!uid || !alert?.id) {
        return;
      }
      const state = readPropertyPageAlertState(alert.id, uid);
      writePropertyPageAlertState(alert.id, uid, {
        visitCount: state.visitCount,
        dismissed: true,
      });
      this.visiblePropertyPageAlerts = this.visiblePropertyPageAlerts.filter(
        (item) => item.id !== alert.id,
      );
    },
    applySearchFromRouteQuery(queryValue) {
      const search = (queryValue ?? this.$route.query.search ?? "")
        .toString()
        .trim();
      this.searchInput = search;
      this.searchQuery = search;
    },
    maybeOpenGuestQAFromRoute() {
      const flag = this.$route.query.guestQA;
      const shouldOpen = flag === "1" || flag === "true";
      if (!shouldOpen || this.guestQAOpenedFromRoute || this.loading) {
        return;
      }
      const search = (this.searchQuery || this.$route.query.search || "")
        .toString()
        .trim()
        .toLowerCase();
      if (!search || !this.filteredProperties.length) {
        return;
      }
      const match = this.filteredProperties.find(
        (prop) =>
          String(prop.propertyId || prop.key || prop.docId || "")
            .toLowerCase() === search,
      );
      if (!match) {
        return;
      }
      this.guestQAOpenedFromRoute = true;
      this.showGuestQABuilder(match.propertyId || match.key || match.docId);
    },
    setUnansweredGuestQuestionCount({ propertyId, count }) {
      const id = String(propertyId || "");
      this.properties = this.properties.map((prop) => {
        if (String(prop.propertyId || prop.key || prop.docId) !== id) {
          return prop;
        }
        return {
          ...prop,
          unansweredGuestQuestionCount: Number(count) || 0,
        };
      });
    },
    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.searchQuery = this.searchInput || "";
      }, 300);
    },
    downloadQRCodeImage() {
      console.log("downloadQRCodeImage in parent", this.$refs.qrCodeComponent);
    },
    onDataUrlChange(dataUrl) {
      console.log("onDataUrlChange", dataUrl);
      this.dataUrl = dataUrl;
    },
    reload() {
      console.log("test reload");
      this.getProperties();
    },
    goToOrg(item) {
      console.log("goToOrg", item);
      if (typeof item === "string") {
        this.$router.push(`/organization/${item}`);
      } else if (item.orgId) {
        this.$router.push(`/organization/${item.orgId}`);
      } else {
        console.log("could not go to org", item);
      }
    },
    formatSidecarSummary(summary) {
      if (!summary) return "";
      return Object.entries(summary)
        .map(([key, value]) => `${key}: ${value}`)
        .join(" | ");
    },
    mergeSidecarSummaries(totals, pageSummary) {
      if (!pageSummary) {
        return totals;
      }
      totals.scanned += pageSummary.scanned || 0;
      totals.viewsCreated =
        (totals.viewsCreated || 0) + (pageSummary.viewsCreated || 0);
      totals.viewsUpdated =
        (totals.viewsUpdated || 0) + (pageSummary.viewsUpdated || 0);
      totals.viewsDeleted =
        (totals.viewsDeleted || 0) + (pageSummary.viewsDeleted || 0);
      totals.unchanged = (totals.unchanged || 0) + (pageSummary.unchanged || 0);
      totals.errors = (totals.errors || 0) + (pageSummary.errors || 0);
      totals.hasMore = pageSummary.hasMore === true;
      totals.resumeAfterPropertyId = pageSummary.resumeAfterPropertyId || null;
      totals.dryRun = pageSummary.dryRun;
      return totals;
    },
    async runPaginatedSidecarCallable(functionName, dryRun = true) {
      const callable = firebase
        .functions()
        .httpsCallable(functionName, { timeout: 120000 });
      let resumeAfter = null;
      let totals = {
        dryRun,
        scanned: 0,
        viewsCreated: 0,
        viewsUpdated: 0,
        viewsDeleted: 0,
        unchanged: 0,
        errors: 0,
        hasMore: false,
        resumeAfterPropertyId: null,
      };
      let pages = 0;

      do {
        pages++;
        const response = await callable({
          dryRun,
          startAfterPropertyId: resumeAfter,
        });
        const data = response?.data || {};
        totals = this.mergeSidecarSummaries(totals, data.summary || null);
        resumeAfter = totals.hasMore ? totals.resumeAfterPropertyId : null;
        if (this.sidecarRunResult) {
          this.sidecarRunResult.message = `Processing page ${pages}... scanned ${totals.scanned}`;
        }
      } while (resumeAfter);

      return totals;
    },
    async runMigratePropertyCardViews(dryRun = true) {
      if (this.runningSidecarJob) {
        return;
      }
      this.runningSidecarJob = true;
      this.sidecarRunResult = {
        success: true,
        title: dryRun
          ? "Card view backfill dry run in progress"
          : "Card view backfill in progress",
        message: "Starting...",
        summary: null,
      };
      try {
        const summary = await this.runPaginatedSidecarCallable(
          "migratePropertyCardViews",
          dryRun,
        );
        this.sidecarRunResult = {
          success: true,
          title: dryRun
            ? "Card view backfill dry run complete"
            : "Card view backfill complete",
          message: dryRun
            ? `Would create ${summary.viewsCreated}, update ${summary.viewsUpdated}, and delete ${summary.viewsDeleted} card view docs.`
            : `Created ${summary.viewsCreated}, updated ${summary.viewsUpdated}, and deleted ${summary.viewsDeleted} card view docs.`,
          summary,
        };
        this.$store.commit("setAlert", {
          type: "success",
          text: dryRun
            ? "Card view backfill dry run completed."
            : "Card view backfill completed.",
        });
        if (!dryRun) {
          this.getProperties();
        }
      } catch (error) {
        this.sidecarRunResult = {
          success: false,
          title: "Card view backfill failed",
          message: error?.message || "Unknown error",
          summary: null,
        };
        this.$store.commit("setAlert", {
          type: "error",
          text: `Card view backfill failed: ${error?.message || "Unknown error"}`,
        });
      } finally {
        this.runningSidecarJob = false;
      }
    },
    setGuestInfo(item) {
      //TODO: make this work with apimixin
      this.$store.dispatch("setGuestInfoSwitchOnProperty", item);
    },
    async copyProperty(item) {
      if (this.propLimit - this.propCount <= 0) {
        console.log("max properties reached");
        this.message = { ...this.maxPropertiesMessage };
        return;
      } else {
        console.log(
          "max properties not reached",
          this.propLimit,
          this.propCount
        );
      }

      try {
        this.message = {
          show: true,
          loading: true,
          persistent: true,
          title: "Copying Property",
        };

        const newPropertyId = await this.$store.dispatch("copyProperty", item);
        // Wait for 3 seconds to give the new property time to be created.
        await new Promise((resolve) => setTimeout(resolve, 3000));

        this.getProperties();

        this.message.show = false;

        return newPropertyId;
      } catch (error) {
        console.error(error);
        throw error;
      }
    },
    transferProperty(item) {
      alert("transfer property is not working yet");
      //TODO: make this work with apimixin
      // console.log("transferProperty", item)
      // this.item = item
      // this.$store.commit("setShowConfirm",true)
      // let message = "Are you sure that you want to transfer this property"
      // if ( this.moveGuestInfo ) { message += " and guest data" } else { message += " without guest data" }
      // message = message + " to " + this.newUid + "?"
      // this.$store.commit("setConfirmMessage", message)
    },
    showPropertyLink(prop) {
      // this.message = {...this.linkMessage}
      this.message = {
        show: true,
        title: "Link Copied",
        prop: prop,
        width: 450,
        link: `${this.$store.getters.appSite}${prop.propertyId}`,
        slotName: "link",
        transition: "dialog-bottom-transition",
      };
      this.copyLink(this.message.link);
      console.log("show property link", prop);
      setTimeout(() => {
        this.message = this.closeMessage;
      }, 3000);
    },
    showPropertyQRCode(prop) {
      // this.message = {...this.linkMessage}
      this.message = {
        show: true,
        title: "Guidebook QR Code",
        prop: prop,
        width: 450,
        link: `${this.$store.getters.appSite}${prop.propertyId}`,
        slotName: "qrcode",
        transition: "dialog-bottom-transition",
        actions: [{ type: "spacer" }, { label: "ok", clickEmit: "cancel" }],
      };
      this.copyLink(this.message.link);
      // console.log('show property link', prop)
    },
    showInterview(propertyId) {
      console.log("showInterview...", propertyId);
      this.$store.commit("toggleTrigger");
      this.message = {
        show: true,
        title: "Set up your digital guide",
        page: 0,
        message: "",
        propertyId: propertyId,
        width: 564,
        slotName: "interview",
        transition: "dialog-bottom-transition",
        persistent: true,
        trigger: this.$store.getters.trigger,
        actions: [
          { type: "spacer" },
        ],
      };
    },
    showTransferProperty(propertyId) {
      console.log("transferProperty", propertyId);
      this.message = {
        show: true,
        title: "Transfer Property",
        message:
          "Enter the destination organization ID to transfer this property.",
        propertyId: propertyId,
        width: 550,
        slotName: "transferProp",
        transition: "dialog-bottom-transition",
        persistent: false,
        actions: [
          { type: "spacer" },
        ],
      };
    },
    goToPreview(propertyId) {
      console.log("goToPreview");
      if (!this.$store.getters.appSettings.showPreviewLink) {
        alert("Preview of new features is not currently available");
        return;
      }
      let previewLink = this.$store.getters.appSettings.previewLink;
      window.open(
        `${previewLink}/login/${propertyId}`,
        "",
        "width=900, height=812"
      );
    },
    goToSubscribePage() {
      this.$router.push(`/premium`);
    },
    goToLocalhost(propertyId) {
      console.log("goToLocalhost");
      let localhostLink = "http://localhost:5000";
      // Check if localhost:5000 is running before opening the window
      window.open(
        `${localhostLink}/login/${propertyId}`,
        "",
        "width=900, height=812"
      );
    },
    closeTransfer() {
      this.message.show = false;
    },
    toWords: function (number) {
      let word = "";
      let numberWords = {
        1: "One",
        2: "Two",
        3: "Three",
        4: "Four",
        5: "Five",
        6: "Six",
        7: "Seven",
        8: "Eight",
        9: "Nine",
        10: "Ten",
        11: "Eleven",
        12: "Twelve",
        13: "Thirteen",
        14: "Fourteen",
        15: "Fifteen",
      };
      word = numberWords[number].toLowerCase() || number;
      return word;
    },
    copyLink(link) {
      navigator.clipboard.writeText(link);
    },
    openListingImport() {
      if (!this.hasListingImportAccess) {
        this.message = {
          show: true,
          title: "Pro feature",
          message:
            "Import from listing is not included in this organization's plan.",
          actions: [
            { type: "spacer" },
            { label: "Upgrade", clickEmit: "cancel", color: "primary" },
            { label: "Close", clickEmit: "cancel" },
          ],
        };
        this.goToSubscribePage();
        return;
      }
      if (this.count >= this.limit) {
        this.message = { ...this.maxPropertiesMessage };
        return;
      }
      this.showListingImport = true;
    },
    onListingImportCreated({ propertyId }) {
      this.showListingImport = false;
      this.$emit("addProperty", true);
      setTimeout(() => {
        this.getProperties();
      }, 1500);
      this.$store.commit("setAlert", {
        show: true,
        type: "success",
        message: `Draft guidebook ${propertyId} created from listing. Open the editor to finish details, then publish.`,
      });
    },
    async addProperty() {
      console.log("addProperty button pushed");
      const orgObj = this.orgObj;

      // let limit = this.gcvOrg.account.subscriptionCount;
      // limit = typeof limit === "string" ? parseInt(limit, 10) : limit;

      // let count = "propCount" in orgObj ? orgObj.propCount : 0;

      let orgId = orgObj.orgId;
      let uid = this.user.uid;
      let orgName = orgObj.name;

      this.message = { ...this.maxPropertiesMessage };

      if (this.count < this.limit) {
        this.message = {
          show: true,
          loading: true,
          persistent: true,
          title: "Adding Property",
        };
        this.loading = true;
        try {
          const result = await this.apiMakeNewProperty(orgId, uid, orgName);
          if (!result?.propertyId) {
            throw new Error("Property was not created.");
          }
          this.propertyId = result.propertyId;
          this.$emit("addProperty", true);
          this.message = {
            show: false,
          };
          setTimeout(() => {
            this.getProperties();
          }, 2000);

          const sendToArr = ["support@everhost.io"];
          if (this.sendNewPropertyEmail) {
            this.sendEmail(orgName, orgId, uid, sendToArr);
          }
          console.log("show interview for propertyId", this.propertyId);
          if (this.showInterviewOnNewProperty) {
            this.$nextTick(() => {
              this.showInterview(result.propertyId);
            });
          }
        } catch (error) {
          console.error("addProperty failed", error);
          this.message = { show: false };
          this.$store.commit("setAlert", {
            type: "error",
            text: `Failed to add property: ${error?.message || "Unknown error"}`,
          });
        } finally {
          this.loading = false;
        }
      } else {
        this.message = { ...this.maxPropertiesMessage };
      }
    },
    sendEmail(orgName, orgId, uid, sendToArr) {
      console.log("sendEmail about new property creation");
      try {
        let mailObj = {
          from: {
            email: "support@everhost.io",
          },
          to: [{ email: "tomavey@outlook.com", name: "Tom Avey" }],
          subject: "Everhost property initiated by " + orgName,
          createdAt: Date.now().toLocaleString("en-US", {
            timeZone: "America/New_York",
          }),
          html: `
                            <html>
                            <body>
                              -----<br>
                            A new property was created for ${orgName} by ${uid}<br>
                            Orgid: ${orgId}<br>
                            </body>
                            </html>
                        `,
        };

        console.log("property created email", mailObj);
        this.$store.dispatch("mailerSend", mailObj);
      } catch (error) {
        console.log("error", error);
      }
    },
    async getTemplatePropertyIds() {
      const ids = new Set();
      const defaultId =
        this.$store.getters.defaultPropertyId || "default12821";
      ids.add(String(defaultId));

      const settingsMap =
        this.$store.getters.appSettings?.propertyTypeTemplates || {};
      Object.values(settingsMap).forEach((raw) => {
        const id = String(raw || "").trim();
        if (id) {
          ids.add(id);
        }
      });

      try {
        const snap = await firebase
          .firestore()
          .collection("properties")
          .where("isPropertyTemplate", "==", true)
          .get();
        snap.forEach((doc) => ids.add(doc.id));
      } catch (error) {
        console.warn("Failed to load template property ids", error);
      }

      return ids;
    },
    async getProperties() {
      this.loading = true;
      this.apiClearDocSubscriptions();

      try {
        let query = firebase.firestore().collection("propertyCardViews");

        if (!this.showRetrieveAllOption && this.orgObj && this.orgObj.orgId) {
          query = query.where("orgId", "==", this.orgObj.orgId);
        }

        query = query.orderBy("createdAt", "desc");

        const [snapshot, templateIds] = await Promise.all([
          query.get({ source: "server" }),
          this.getTemplatePropertyIds(),
        ]);
        this.properties = snapshot.docs
          .map((doc) => {
            const item = doc.data();
            item.docId = doc.id;
            item.key = item.key || doc.id;
            return item;
          })
          .filter((item) => {
            if (item.isPropertyTemplate === true) {
              return false;
            }
            const id = String(item.propertyId || item.key || item.docId || "");
            return !templateIds.has(id);
          });
      } catch (error) {
        console.error("getProperties failed", error);
        this.$store.commit("setAlert", {
          type: "error",
          text:
            "Failed to load properties: " + (error.message || "Unknown error"),
        });
      } finally {
        // Vue batches the property assignment and loading-state update. Wait for
        // the cards to be added to the DOM and painted before hiding the bar.
        await this.$nextTick();
        await new Promise((resolve) => {
          requestAnimationFrame(() => requestAnimationFrame(resolve));
        });
        this.loading = false;
        this.maybeOpenGuestQAFromRoute();
      }
    },
    nextInterview() {
      console.log("nextInterview", this.message.formData);
      this.updateProperty();
      this.message.page++;
    },
    closeInterview() {
      this.message.show = false;
      this.message.page = 0;
      this.message.formData = {};
      this.getProperties();
    },
    //TODO: this is a duplicate of the one in ehc-interview
    updateProperty: async function () {
      let obj = {};
      let formData = this.message.formData;
      let propertyId = this.propertyId || formData.propertyId;
      if (formData.name) {
        obj.name = formData.name;
      }
      if (formData.avatarImg) {
        obj.avatarImg = formData.avatarImg;
      }
      if (formData.mapAddress) {
        obj.mapAddress = formData.mapAddress;
      }
      if (formData.unit_of_temperature) {
        obj.unit_of_temperature = formData.unit_of_temperature;
      }
      if (formData.backgroundColor) {
        obj.backgroundColor = formData.backgroundColor;
      }
      if (formData.noguestinfo) {
        obj.noguestinfo = formData.noguestinfo;
      }
      if (formData.bookagain) {
        obj.bookagain = formData.bookagain;
      }

      await this.apiUpdateProperty(propertyId, obj);
      console.log("updated property: ", obj);
    },
  },
  //TODO: this doesnt work well -- Grant fix
  beforeDestroy() {
    clearTimeout(this.searchTimeout);
    // console.log("beforeDestroy ehc properties");
    try {
      this.apiUnsub.forEach((unsub) => {
        unsub();
      });
    } catch (error) {
      console.log("something went wrong unsubscribing");
    }
  },
};
</script>

<style>
.qrcode canvas {
  width: 100% !important;
  height: auto !important;
}
</style>
